import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import LabeledSwitch from "../../../../../components/LabeledSwitch";
// Speciality column removed from template list
// import MultiSpecialty from "../../../../../components/MultiSpecialty";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import CustomTitle from "../../../../../components/app/CustomTitle";
import DeleteModal from "../../../../../components/app/DeleteModal";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useNewTemplateCreate } from "../../../../../queries/template/new-flow/use-new-template-create";
import { useFormTemplateArchiveStatusV2 } from "../../../../../queries/template/new-flow/use-new-template-delete";
import { useNewTemplateList } from "../../../../../queries/template/new-flow/use-new-template-list";
// import { useImportDefaultTemplates } from "../../../../../queries/template/use-import-default-templates";
import { GlobalTableHeightWrapper } from "../../../../../components/GlobalTableHeightWrapper";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  // getErrorSnackbar,
  // getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
  showToastOnApiCall,
  // hasActiveFilters,
  transformMultiSelectFrontEndVal,
} from "../../../../../utils/utils";
import CopyFormModal from "../form-builder/copyFormModal";
import {
  FormActionValue,
  TEMPLATE_TYPE_LIST_MAPPER,
} from "../form-builder/form-builder-constant";
import TemplateNewForm from "./TemplateNewForm";
import TemplateFilter, {
  TemplateFilterOptions,
  templateFilterInitialValues,
} from "./filter/TemplateFilter";
import NewTemplatePreview from "./template-preview/NewTemplatePreview";
import SearchInput from "../../../../../components/SearchInput";
import { getViewAs } from "../../../../../utils/auth-storage";

type TemplatesDataType = {
  id: string;
  name: string;
  customForm: { [k in string]: string };
  speciality: string;
  templateName: string;
  createdBy: string;
  modifiedDate: string;
  icdCodes: any;
  updateDate?: string;
};

/**
 * @author [Rajkumar Rathod]
 */
const NewTemplateTable = () => {
  const COLUMNS = applyDefaultColumnSettings<TemplatesDataType>(
    [
      {
        field: "templateName",
        flex: 0.5,
        headerName: "Template Name",
        minWidth: 220,
        renderCell: ({ value, row }) => (
          <Text
            isTabletext
            color={"primary"}
            sx={{ cursor: "pointer" }}
            onClick={() => handleActionClick("view", row)}
          >
            {value}
          </Text>
        ),
      },
      // {
      //   field: "templateTypes",
      //   flex: 0.4,
      //   headerName: "Template Type",
      //   minWidth: 188,
      //   renderCell: ({ value }) => (
      //     <Text>{TEMPLATE_TYPE_LIST_MAPPER[value]}</Text>
      //   ),
      // },
      {
        field: "templateTypes",
        flex: 0.4,
        headerName: "Template Type",
        minWidth: 188,
        renderCell: ({ value }) => (
          <Text isTabletext>{TEMPLATE_TYPE_LIST_MAPPER[value]}</Text>
        ),
      },
      // {
      //   field: "form",
      //   headerName: "Form",
      //   flex: 0.4,
      //   minWidth: 220,
      //   renderCell: ({ row }) => (
      //     <Text>
      //       {row?.customForm ? Object.values(row?.customForm)?.[0] : "-"}
      //     </Text>
      //   ),
      // },
      // {
      //   field: "speciality",
      //   flex: 0.4,
      //   headerName: "Speciality",
      //   minWidth: 160,
      //   sortable: false,
      //   renderCell: ({ value }) => (
      //     <MultiSpecialty specialties={value} width={280} />
      //   ),
      // },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 0.4,
        minWidth: 200,
        renderCell: ({ value }) => (value && value?.trim() ? value : "-"),
      },
      {
        field: "modified",
        flex: 0.3,
        headerName: "Updated Date",
        width: 150,
        renderCell: ({ row }) =>
          row?.updateDate ? formatDate(row?.updateDate) : "-",
      },

      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }: any) => (
          <ActionMenu
            options={[
              { value: "view", label: "View" },
              { value: "update", label: "Update" },
              {
                value: "make_copy",
                isDisabled:
                  row?.formType === "EMAIL" || row?.formType === "SMS",
                hoverMsg: "Feature will Available Soon",
                label: "Make a Copy",
              },
              {
                value: "archive",
                label: archiveFilters ? "Unarchive" : "Archive",
              },
            ]}
            onActionClick={async (value) => {
              const v = value as FormActionValue;
              handleActionClick(v, row);
            }}
          />
        ),
      },
    ],
    ["modified"]
  );

  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [filters, setFilters] = useState<TemplateFilterOptions>(
    templateFilterInitialValues
  );

  // todo: if in future we want to add archive filter checkbox then we can add it here.
  const [archiveFilters, setArchiveFilters] = useState<boolean>(false);
  const [searchKey, setSearchKey, insVal] = useDebounce<string>("");

  const memoizedSpecialityUuids = useMemo(
    () =>
      filters?.specialties?.reduce((acc, cur: { value: string }) => {
        acc.push(cur.value);
        return acc;
      }, [] as Array<string>),
    [filters?.specialties]
  );

  const {
    data,
    refetch: refetchTableLatestData,
    isLoading: isTableDataLoading,
    isFetching: isTableDataFetching,
  } = useNewTemplateList({
    pagination,
    gridSorting,
    searchKey: searchKey,
    specialityUuids: memoizedSpecialityUuids,
    templateType: filters?.templateType as any,
    xTENANTID: getXtenantId(),
    archive: archiveFilters,
  });

  const { mutateAsync: archiveForm } = useFormTemplateArchiveStatusV2();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveForm);

  const makeCopyDisclosureHost = useDisclosure(false);
  const showSnackbar = useSnackbar();

  const [curFormSchema, setCurFormSchema] = useState<any>();
  const isLoadingForm = isTableDataLoading || isTableDataFetching;
  const { mutateAsync: createTemplate } = useNewTemplateCreate();
  // const { mutateAsync: importDefaultTemplates } = useImportDefaultTemplates();

  const navigate = useNavigate();
  const previewTemplateDisclosure = useDisclosure();
  const [isFormPreviewEdit, setIsFormPreviewEdit] = useState<boolean>(false);

  async function handleActionClick(
    action: FormActionValue,
    row?: TemplatesDataType & { uuid?: string }
  ) {
    const transformedTemplateData = {
      ...row,
      speciality: isEmpty(row?.speciality)
        ? undefined
        : transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            (row as any).speciality
          ),
      icdCodes: isEmpty(row?.icdCodes)
        ? undefined
        : transformMultiSelectFrontEndVal(
            DEFAULT_VALUE_LABEL_LIST,
            (row as any).icdCodes
          ),
    };

    setCurFormSchema(transformedTemplateData);

    if (action !== "archive") refetchTableLatestData();

    switch (action) {
      case "view":
        previewTemplateDisclosure.open();
        break;
      case "update":
        handlePreviewEdit();
        break;
      case "archive":
        deleteDisc.open(row?.uuid);
        break;
      case "make_copy":
        makeCopyDisclosureHost.open(row?.templateName);
        break;
      default:
        break;
    }
  }

  const isProvider = getViewAs() === "provider";

  function handleNavigate() {
    navigate(
      isProvider
        ? "/app/provider/library/templates-host"
        : "/app/library/templates-host"
    );
  }

  const handleMakeCopy = (templateName: string) => {
    const transformedPayload = {
      rosTemplate: {
        ...getUnNecessaryTransformData(
          ["speciality", "icdCodes"],
          curFormSchema
        ),
        templateName,
      },
      xTENANTID: getXtenantId(),
    };

    showToastOnApiCall({
      showSnackbar,
      apiCall: createTemplate(transformedPayload),
      onSave: () => {
        makeCopyDisclosureHost.close();
        refetchTableLatestData();
      },
    });
  };

  // const handleImportTemplates = () => {
  //   importDefaultTemplates(getXtenantId())
  //     .then((res) => {
  //       refetchTableLatestData();
  //       showSnackbar(getSuccessSnackbar(res));
  //     })
  //     .catch((err) => showSnackbar(getErrorSnackbar(err)));
  // };

  function handlePreviewEdit() {
    setIsFormPreviewEdit((prev) => !prev);

    previewTemplateDisclosure.opened && previewTemplateDisclosure.close();

    if (isFormPreviewEdit) {
      refetchTableLatestData();
    }
  }

  return (
    <>
      {!isFormPreviewEdit ? (
        <>
          <Flex justifyContent={"space-between"}>
            <CustomTitle color={"black"}>Templates</CustomTitle>
            <Flex gap={2.5} alignItems={"center"}>
              <LabeledSwitch
                label="Show Archived Templates"
                checked={archiveFilters}
                onChange={() => setArchiveFilters(!archiveFilters)}
              />
              <Box width={"16rem"}>
                {/* <TextField
                  placeholder={"Search By Template Name"}
                  fullWidth
                  variant="outlined"
                  value={insVal ?? ""}
                  name="patientName"
                  size="small"
                  sx={{ fontSize: "14px" }}
                  autoComplete="off"
                  onChange={(e) => setSearchKey?.(e.target.value ?? "")}
                  InputProps={{
                    sx: {
                      "& .MuiInputBase-input": { fontSize: "14px" },
                    },
                  }}
                /> */}
                <SearchInput
                  searchKey={insVal}
                  onSearch={(val) => setSearchKey(val)}
                  placeholder="Search By Template Name"
                />
              </Box>
              <TemplateFilter onApply={setFilters} filters={filters} />

              {/* {isProvider && (
                <StyledButtonV2
                  shouldAddIcon
                  variant="outlined"
                  onClick={handleImportTemplates}
                  label="Import Default Templates"
                />
              )} */}

              <StyledButtonV2
                shouldAddIcon
                onClick={handleNavigate}
                label="Create New Template"
              />
            </Flex>
          </Flex>

          <Box width={"100%"}>
            <DeleteModal
              w="380px"
              id={deleteDisc?.deleteId}
              open={deleteDisc.opened}
              onClose={deleteDisc.close}
              onConfirmation={(id) =>
                archiveHandler(!archiveFilters, id).then((res) => {
                  refetchTableLatestData();
                  return res;
                })
              }
              content={`You want to ${
                archiveFilters ? "Unarchive" : "Archive"
              } this Template`}
            />
            {/* <PreviewTemplateDrawer
              isLoading={isLoadingForm}
              disClosureHost={previewTemplateDisclosure}
              title={
                FORM_TYPE_MAPPER[curFormSchema?.formType] ?? "Preview Template"
              }
              schema={curFormSchema}
              onEdit={handlePreviewEdit}
              formData={curFormSchema?.formData}
              smsEmailData={
                selectedTemplate?.emailSmsTemplateId &&
                (curFormSchema?.formType === "EMAIL" ||
                  curFormSchema?.formType === "SMS")
                  ? smsEmailApiData?.data
                  : null
              }
            /> */}
            {previewTemplateDisclosure.opened && (
              <NewTemplatePreview
                disClosureHost={previewTemplateDisclosure}
                preFillSchema={curFormSchema}
                onEdit={handlePreviewEdit}
              />
            )}

            {makeCopyDisclosureHost.opened && (
              <CopyFormModal
                inputLabel="Template Name"
                isLoading={isLoadingForm}
                prefillName={makeCopyDisclosureHost?.data}
                onConfirm={handleMakeCopy}
                disclosureHost={makeCopyDisclosureHost}
              />
            )}
            {/* <ActiveFilters filters={filters} setFilters={setFilters} /> */}
            <GlobalTableHeightWrapper>
              <CustomDataGridV2
                loading={isTableDataLoading || isTableDataFetching}
                rows={data?.data?.content ?? []}
                onSortModelChange={setGridSorting}
                getRowId={(row) => row?.uuid}
                columns={COLUMNS}
                paginationModel={pagination}
                onPaginationModelChange={setPagination}
                rowCount={data?.data?.page?.totalElements}
                disableRowSelectionOnClick
              />
            </GlobalTableHeightWrapper>
          </Box>
        </>
      ) : (
        <TemplateNewForm
          onClose={handlePreviewEdit}
          preFillSchema={curFormSchema}
          isLoading={isLoadingForm}
        />
      )}
    </>
  );
};

export default NewTemplateTable;
